import type { SymbolAsset, SymbolGraphic } from './model';
import { isBuiltinGraphic } from './model';
import { backingKey, type SymbolPresentation } from './backing';

const eligibility = new WeakMap<Uint8Array, boolean>();
/** Conservative contract: one effective visible RGB paint, with any alpha variations.
 * Only canonical, validated static SVG enters here. Unknown equivalent colour spellings
 * can make an SVG ineligible; they never cause multicolour artwork to be recoloured.
 */
export function monochromeSvg(asset: SymbolAsset): boolean {
  if (asset.mimeType !== 'image/svg+xml') return false;
  const cached = eligibility.get(asset.bytes);
  if (cached !== undefined) return cached;
  const doc = new DOMParser().parseFromString(
    new TextDecoder().decode(asset.bytes),
    'image/svg+xml',
  );
  const paints = new Set<string>();
  const canonical = (paint: string) => {
    const v = paint.toLowerCase().replace(/\s/g, '');
    if (['none', 'transparent', '#0000', '#00000000'].includes(v)) return null;
    const names: Record<string, string> = {
      black: '#000000',
      white: '#ffffff',
      red: '#ff0000',
      blue: '#0000ff',
      green: '#008000',
    };
    if (names[v]) return names[v]!;
    if (/^#[0-9a-f]{3,4}$/.test(v))
      return (
        '#' +
        v
          .slice(1, 4)
          .split('')
          .map((c) => c + c)
          .join('')
      );
    if (/^#[0-9a-f]{6}(?:[0-9a-f]{2})?$/.test(v)) return v.slice(0, 7);
    const rgb = /^rgba?\((\d+),(\d+),(\d+)(?:,([\d.]+))?\)$/.exec(v);
    if (rgb)
      return rgb[4] === '0'
        ? null
        : '#' +
            rgb
              .slice(1, 4)
              .map((n) => Number(n).toString(16).padStart(2, '0'))
              .join('');
    return v;
  };
  const walk = (el: Element, fill = '#000000', stroke = 'none', visible = true) => {
    fill = el.getAttribute('fill') ?? fill;
    stroke = el.getAttribute('stroke') ?? stroke;
    visible = visible && el.getAttribute('opacity') !== '0';
    if (
      visible &&
      ['path', 'rect', 'circle', 'ellipse', 'line', 'polygon', 'polyline'].includes(el.localName)
    ) {
      if (el.localName !== 'line' && el.getAttribute('fill-opacity') !== '0') {
        const colour = canonical(fill);
        if (colour) paints.add(colour);
      }
      if (el.getAttribute('stroke-opacity') !== '0' && el.getAttribute('stroke-width') !== '0') {
        const colour = canonical(stroke);
        if (colour) paints.add(colour);
      }
    }
    for (const child of el.children) walk(child, fill, stroke, visible);
  };
  walk(doc.documentElement);
  const result = paints.size === 1;
  eligibility.set(asset.bytes, result);
  return result;
}
export function graphicTint(
  graphic: SymbolGraphic,
  colour: string,
  tint?: string | null,
): string | undefined {
  return isBuiltinGraphic(graphic) ? colour : (tint ?? undefined);
}
export function graphicKey(
  graphic: SymbolGraphic,
  tint?: string,
  presentation?: SymbolPresentation,
): string {
  if (typeof graphic === 'string') return graphic;
  const id = graphic.kind === 'asset' ? graphic.assetId : graphic.id;
  const base = tint ? `${id}@${tint.toLowerCase()}` : id;
  const backing = backingKey(presentation);
  return backing ? `${base}@backing:${backing}` : base;
}
